/*
 * Still Water notes.
 *
 * Numbers footnote references by first appearance on the page. Notes that are
 * never referenced stay visible (unnumbered) so no compliance text disappears
 * because a reference is missing.
 *
 * DOM contract:
 *   <sup class="sw-ref"><a href="#sw-note-KEY" data-sw-ref="KEY" data-sw-ref-label="Note">*</a></sup>
 *   <ol data-sw-notes>
 *     <li id="sw-note-KEY" data-sw-note="KEY">Text <a data-sw-back hidden>…</a></li>
 *   </ol>
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') {
    window.SwNotes = api;
    api.autoInit();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const normalise = (key) => String(key ?? '').trim().toLowerCase();

  /**
   * Works out note numbering without touching the DOM.
   *
   * @param {Array<string|null|undefined>} refKeys - note keys in the order references appear
   * @param {Array<string|null|undefined>} noteKeys - note keys in the order notes are listed
   * @returns {{ numbered: string[], unnumbered: string[], numbers: Record<string, number>, orphanRefs: string[] }}
   */
  function numberNotes(refKeys, noteKeys) {
    const available = new Set(noteKeys.map(normalise).filter(Boolean));
    const numbered = [];
    const orphanRefs = [];

    for (const raw of refKeys) {
      const key = normalise(raw);
      if (!key) continue;
      if (available.has(key)) {
        if (!numbered.includes(key)) numbered.push(key);
      } else if (!orphanRefs.includes(key)) {
        orphanRefs.push(key);
      }
    }

    return {
      numbered,
      unnumbered: [...available].filter((key) => !numbered.includes(key)),
      numbers: Object.fromEntries(numbered.map((key, index) => [key, index + 1])),
      orphanRefs,
    };
  }

  const updateReferences = (refs, numbers) => {
    const firstSeen = new Set();
    refs.forEach((ref) => {
      const key = normalise(ref.dataset.swRef);
      const holder = ref.closest('.sw-ref') || ref;
      const number = numbers[key];
      if (!number) {
        holder.hidden = true;
        return;
      }
      holder.hidden = false;
      ref.textContent = String(number);
      ref.setAttribute('aria-label', `${ref.dataset.swRefLabel || 'Note'} ${number}`);
      if (firstSeen.has(key)) {
        ref.removeAttribute('id');
      } else {
        firstSeen.add(key);
        ref.id = `sw-ref-${key}`;
      }
    });
  };

  const updateList = (list, result) => {
    const items = Array.from(list.querySelectorAll(':scope > [data-sw-note]'));
    const byKey = new Map(items.map((item) => [normalise(item.dataset.swNote), item]));
    [...result.numbered, ...result.unnumbered].forEach((key) => {
      const item = byKey.get(key);
      if (!item) return;
      const number = result.numbers[key];
      if (number) {
        item.dataset.swNumber = String(number);
        delete item.dataset.swUnnumbered;
      } else {
        item.dataset.swUnnumbered = '';
        delete item.dataset.swNumber;
      }
      const back = item.querySelector('[data-sw-back]');
      if (back) {
        back.hidden = !number;
        if (number) back.setAttribute('href', `#sw-ref-${key}`);
      }
      list.appendChild(item);
    });
    list.dataset.swNumbered = 'true';
  };

  /**
   * Applies numbering to every reference and notes list in the document.
   * Numbering is page-wide, so it always scans the whole document.
   *
   * @param {Document} [doc=document]
   * @returns {void}
   */
  function applyNotes(doc) {
    const scope = doc || document;
    const refs = Array.from(scope.querySelectorAll('[data-sw-ref]'));
    const lists = Array.from(scope.querySelectorAll('[data-sw-notes]'));
    const noteKeys = lists.flatMap((list) =>
      Array.from(list.querySelectorAll(':scope > [data-sw-note]')).map((item) => item.dataset.swNote)
    );
    const result = numberNotes(
      refs.map((ref) => ref.dataset.swRef),
      noteKeys
    );
    updateReferences(refs, result.numbers);
    lists.forEach((list) => updateList(list, result));
  }

  /** Runs numbering on load and whenever the theme editor re-renders a section. */
  function autoInit() {
    if (typeof document === 'undefined') return;
    const run = () => applyNotes(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
    document.addEventListener('shopify:section:load', run);
    document.addEventListener('shopify:section:unload', () => window.setTimeout(run, 0));
  }

  return { numberNotes, applyNotes, autoInit };
});
